import {formatCopy} from 'utils/typography';
import React, {useCallback, useEffect, useRef, useState} from 'react'
import moment from "moment";
import {useTranslation} from "react-i18next";
import { useWatch, useFormContext } from 'react-hook-form'
import cx from "classnames";

import { Cached as CachedIcon, LinkPopover } from '@vedmarket/ui-kit';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from "@mui/material/IconButton";
import { Search as SearchIcon } from '@vedmarket/ui-kit';
import CircularProgress from '@mui/material/CircularProgress';

import DateInput from './date_input';
import LocationInput from './location_input';

import {COLORS} from "../../../../../../../config/constants";
import { useLazyGetLocationsQuery } from "../../../../../../../api/locations";
import {useDemoDeliverySearchForm} from 'components/cabinet/demo/steps/delivery/use-delivery';

import {DELIVERY_FORM, DeliveryFormFields} from "../../index";

import './searchForm.scss'
import {SORTING_OPTIONS} from "../modalSearch";
import { reachGoal } from 'utils/metrica';

type Props = {
    demoRoute?: Route
    isBigInputs?: boolean
    onSend?(data: DeliveryRouteParamsMain): void
}

type OptionData = {
    label: string
}

type LocationItemDefaultCity = LocationItem & OptionData;

const SearchForm = ( { demoRoute, isBigInputs, onSend }: Props) => {

  const [ changeCitiesButtonIsActive, setChangeCitiesButtonIsActive ] = useState<boolean>(false);
  const [ searchButtonIsActive, setSearchButtonIsActive ] = useState<boolean>(false);

  const [ defaultCityFrom, setDefaultCityFrom ] = useState<LocationItemDefaultCity | undefined>();
  const [ defaultCityTo, setDefaultCityTo ] = useState<LocationItemDefaultCity | undefined>();
  const [ showProgressBar, setShowProgressBar ] = useState(isBigInputs);
  const initialDefaultLocationsLoaded = useRef(false);

  const [ locationsRequest ] = useLazyGetLocationsQuery()

  const { t } = useTranslation()

  const {
    getValues,
    setValue,
    control,
    formState: { errors },
  } = useFormContext<DeliveryFormFields>();

  const fromId = useWatch({ control, name: DELIVERY_FORM.LOCATION_FROM });
  const toId  = useWatch({ control, name: DELIVERY_FORM.LOCATION_TO });
  const plannedShipmentDate = useWatch({ control, name: DELIVERY_FORM.PLANNED_SHIPMENT_DATE });

  const setLocationFrom = useCallback((id: string) => {
    setValue(DELIVERY_FORM.LOCATION_FROM, id);
  }, [ setValue ]);

  const setLocationTo = useCallback((id: string) => {
    setValue(DELIVERY_FORM.LOCATION_TO, id);
  }, [ setValue ]);

  const demoPlayback = useDemoDeliverySearchForm({
    demoRoute,
    setLocationFrom,
    setLocationTo,
    setValue,
  })

  useEffect(() => {
    let isCurrent = true;

    const mapCities = (data?: LocationItem[]) => {
      if (!data) return;
      return data.map((item: LocationItem) => ({
        ...item,
        label: [ `${item.name},`, item.country?.name, item.city?.name ].filter(Boolean).join(' ')
      }));
    }

    const locationFromValue = getValues()[DELIVERY_FORM.LOCATION_FROM];
    const locationToValue = getValues()[DELIVERY_FORM.LOCATION_TO];
    const defaultLocationRequests = [];

    const shouldShowInitialLoader = Boolean(isBigInputs && !initialDefaultLocationsLoaded.current);

    if (shouldShowInitialLoader) {
      setShowProgressBar(true);
    }

    if (locationFromValue) {
      const id = locationFromValue
      defaultLocationRequests.push(
        // @ts-ignore
        locationsRequest({id: id}).then((response) => {
          if (isCurrent) {
            setDefaultCityFrom(mapCities(response?.data)?.[0])
          }
        })
      );
    } else {
      setDefaultCityFrom(undefined);
    }

    if (locationToValue) {
      const id = locationToValue;
      defaultLocationRequests.push(
        // @ts-ignore
        locationsRequest({id: id}).then((response) => {
          if (isCurrent) {
            setDefaultCityTo(mapCities(response?.data)?.[0])
          }
        })
      );
    } else {
      setDefaultCityTo(undefined);
    }

    Promise.all(defaultLocationRequests).finally(() => {
      if (shouldShowInitialLoader && isCurrent) {
        setTimeout(() => {
          if (!isCurrent) {
            return;
          }

          initialDefaultLocationsLoaded.current = true;
          setShowProgressBar(false)
        }, 1000);
      }
    });

    return () => {
      isCurrent = false;
    };
  }, [ fromId, getValues, isBigInputs, locationsRequest, toId ])

  const changeCities = async () => {

    const currentDefaultCityFrom = defaultCityFrom;
    setDefaultCityFrom(defaultCityTo);
    setDefaultCityTo(currentDefaultCityFrom);

    const valueFrom = getValues()[DELIVERY_FORM.LOCATION_FROM]
    const valueTo = getValues()[DELIVERY_FORM.LOCATION_TO]
    setValue(DELIVERY_FORM.LOCATION_FROM, valueTo)
    setValue(DELIVERY_FORM.LOCATION_TO, valueFrom)
  }

  useEffect(() => {
    const hasCityFrom = Boolean(fromId);
    const hasCityTo = Boolean(toId);
    const hasValidPlannedShipmentDate =
      Boolean(plannedShipmentDate) &&
      moment(plannedShipmentDate).isValid() &&
      !errors[DELIVERY_FORM.PLANNED_SHIPMENT_DATE];

    setChangeCitiesButtonIsActive(hasCityFrom && hasCityTo);
    setSearchButtonIsActive(hasCityFrom && hasCityTo && hasValidPlannedShipmentDate);
  }, [ defaultCityFrom, defaultCityTo, errors, fromId, plannedShipmentDate, toId ])

  const handleSend = () => {
    reachGoal({
      goal: isBigInputs ? 'naiti_variant_dostavki_m' : 'naiti_variant_dostavki',
      params: {
        location_from: getValues(DELIVERY_FORM.LOCATION_FROM),
        location_to: getValues(DELIVERY_FORM.LOCATION_TO),
        planned_shipment_date: getValues(DELIVERY_FORM.PLANNED_SHIPMENT_DATE),
      },
    });

    if (!onSend){
      return
    }

    onSend({
      [DELIVERY_FORM.LOCATION_FROM]: fromId,
      [DELIVERY_FORM.LOCATION_TO]: toId,
      [DELIVERY_FORM.PLANNED_SHIPMENT_DATE]: moment(getValues()[DELIVERY_FORM.PLANNED_SHIPMENT_DATE]).format('YYYY-MM-DD'),
      order_by: SORTING_OPTIONS.DELIVERY_DESK
    });

  }

  return (
    <form
      id='delivery'
      {...demoPlayback.formProps}
      className={cx('delivery-search-form', { 'delivery-search-form_modal': isBigInputs })}
    >
      {showProgressBar ? <CircularProgress /> : (
        <>
          <div className='searchForm'>
            <LocationInput
              demoValue={demoRoute?.location_from}
              fieldName={DELIVERY_FORM.LOCATION_FROM}
              label={t('common__text__fromCity')}
              placeholder={t('calc__text__delivery__exampleShanghai')}
              isBigInput={isBigInputs}
              // @ts-ignore
              defaultValue={defaultCityFrom}
            />
            <div className={cx('searchForm__line', {
              'searchForm__line_isBig': isBigInputs,
            })}>
              <span className='searchForm__line-image' aria-hidden />
              <IconButton
                className='searchForm__change-button'
                aria-label={t('calc__text__delivery__swapCities')}
                sx={{
                  position: 'absolute',
                  top: '16px',
                  right: '-20px',
                  border: '1px solid currentColor',
                  background: COLORS.WHITE,
                  color: COLORS.BLUE,
                  width: '40px',
                  height: '40px',
                  padding: '8px',
                  borderRadius: '50%',
                  '&:hover': { background: COLORS.LIGHT_BLUE },
                }}
                onClick={changeCities}
                disabled={!changeCitiesButtonIsActive}
              >
                <CachedIcon
                  style={{
                    borderRadius: '50%',
                  }}  />
              </IconButton>
            </div>
            <div className='delivery-search-form__destination'>
              <LocationInput
                demoValue={demoRoute?.location_to}
                fieldName={DELIVERY_FORM.LOCATION_TO}
                label={t('common__text__toCity')}
                placeholder={t('calc__text__delivery__exampleMoscow')}
                isBigInput={isBigInputs}
                // @ts-ignore
                defaultValue={defaultCityTo}
              />
              <LinkPopover
                linkText={t('calc__help__deliveryLocationsLink')}
                text={formatCopy(t('calc__help__deliveryLocations'))}
              />
            </div>
            <div className='delivery-search-form__field'>
              <DateInput
                demoDate={demoRoute?.planned_shipment_date}
                isBigInputs={isBigInputs}
              />
              <LinkPopover
                linkText={t('calc__help__shipmentDateLink')}
                text={formatCopy(t('calc__help__shipmentDate'))}
              />
            </div>
          </div>
          <div className='delivery-search-form__action'>
            <Button
              {...demoPlayback.buttonProps}
              startIcon={<SearchIcon />}
              // type='submit'
              disabled={!searchButtonIsActive}
              variant={isBigInputs ? 'contained' : 'outlined'}
              className='delivery-search-form__submit'
              fullWidth
              onClick={handleSend}
            ><Typography variant='button'>{t('actions__text__findDeliveryOptions')}</Typography></Button>
            <LinkPopover
              linkText={t('calc__help__deliveryOptionsLink')}
              text={formatCopy(t('calc__help__deliveryOptions'))}
            />
          </div>
        </>
      )}
    </form>
  )
}

export default SearchForm
